import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Finds the engraving partner of a cart line.
   *
   * @typedef {object} CartItem
   * @property {string} key
   * @property {number} id
   * @property {number} variant_id
   * @property {number} quantity
   * @property {Record<string, string> | null} properties
   * @param {CartItem} cartItem - Cart item from cart.js.
   * @param {{items: CartItem[]}} cart - Cart object from cart.js.
   * @returns {CartItem | null} The related cart item or null.
   */

  #findEngravingPartner(cartItem, cart) {
    const properties = cartItem?.properties;

    if (!properties || typeof properties !== 'object') return null;

    const engravingGroup = properties._engraving_group;

    if (!engravingGroup) return null;

    return (
      cart.items.find(
        (item) =>
          item.key !== cartItem.key &&
          item.properties?._engraving_group === engravingGroup
      ) ?? null
    );
  }

  /**
   * Returns the cart items that have to be removed together.
   *
   * Behaviour:
   * - Normal cart line: remove only itself.
   * - Watch: remove watch + engraving.
   * - Engraving: remove only engraving.
   *
   * @param {number} line - Cart line index.
   * @returns {Promise<object[]>}
   */
  async #getLinesToRemove(line) {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);

    if (!response.ok) {
      throw new Error(`Failed to load cart: ${response.status}`);
    }

    const cart = await response.json();
    const cartItem = cart.items?.[line - 1];

    if (!cartItem) return [];

    const partner = this.#findEngravingPartner(cartItem, cart);

    // This is the separate engraving product.
    const isEngraving =
      !!cartItem.properties?._engraving_for_sku ||
      !!cartItem.properties?.['Engraving Text'];

    // Removing the engraving must NOT remove the watch.
    if (isEngraving) {
      return [cartItem];
    }

    // Removing the watch removes the watch AND its engraving.
    return partner ? [cartItem, partner] : [cartItem];
  }

  /**
   * @param {number} line - Cart line index.
   */
  async onLineItemRemove(line) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);

      if (!response.ok) {
        throw new Error(`Failed to load cart: ${response.status}`);
      }

      const cart = await response.json();
      const cartItem = cart.items?.[line - 1];

      if (!cartItem) return;

      const partner = this.#findEngravingPartner(cartItem, cart);

      const isEngraving =
        !!cartItem.properties?._engraving_for_sku ||
        !!cartItem.properties?.['Engraving Text'];

      // Remember DOM rows before the cart is updated.
      /** @type {HTMLTableRowElement[]} */
      const rowsToRemove = [];

      const row = this.refs.cartItemRows.find(
        (cartItemRow) => cartItemRow.dataset.key === cartItem.key
      );

      if (row) {
        rowsToRemove.push(row);
      }

      /*
      * If the engraving is removed:
      * - remove only the engraving line
      * - keep the watch
      * - remove the engraving-related properties from the watch
      */
      if (isEngraving) {
        const updates = {
          [cartItem.key]: 0,
        };

        this.#disableCartItems();

        const cartItemsComponents =
          document.querySelectorAll('cart-items-component');

        const sectionsToUpdate = new Set([this.sectionId]);

        cartItemsComponents.forEach((item) => {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionsToUpdate.add(item.dataset.sectionId);
          }
        });

        const body = JSON.stringify({
          updates,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

        const { cartTotal } = this.refs;
        cartTotal?.shimmer();

        // First remove the engraving line.
        const removeResponse = await fetch(
          `${window.Shopify.routes.root}cart/update.js`,
          fetchConfig('json', { body })
        );

        const removeResult = await removeResponse.json();

        if (removeResult.errors) {
          console.error('Cart update error:', removeResult.errors);
          return;
        }

        /*
        * The engraving line is now gone.
        *
        * If there is a watch partner, remove the engraving-specific
        * properties from that watch line.
        */
        if (partner) {
          const changeBody = JSON.stringify({
            id: partner.key,
            quantity: partner.quantity,
            properties: {
              _engraving_group: '',
              _VAS_for_line_No: '',
            },
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          });


          const changeResponse = await fetch(
            `${window.Shopify.routes.root}cart/change.js`,
            fetchConfig('json', { body: changeBody })
          );

          const changeResult = await changeResponse.json();

          if (changeResult.errors) {
            console.error(
              'Failed to clean engraving properties:',
              changeResult.errors
            );
            return;
          }

          resetShimmer(this);

          const newSectionHTML = new DOMParser().parseFromString(
            changeResult.sections?.[this.sectionId] || '',
            'text/html'
          );

          const newCartHiddenItemCount =
            newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;

          const newCartItemCount = newCartHiddenItemCount
            ? parseInt(newCartHiddenItemCount, 10)
            : 0;

          this.dispatchEvent(
            new CartUpdateEvent(
              changeResult,
              this.sectionId,
              {
                itemCount: newCartItemCount,
                source: 'cart-items-component',
                sections: changeResult.sections,
              }
            )
          );

          if (rowsToRemove.length > 0) {
            rowsToRemove.forEach((row) => {
              if (prefersReducedMotion()) return;

              row.style.setProperty(
                '--row-height',
                `${row.clientHeight}px`
              );

              row.classList.add('removing');
            });
          }

          if (changeResult.sections?.[this.sectionId]) {
            morphSection(
              this.sectionId,
              changeResult.sections[this.sectionId]
            );
          }

          document
            .querySelectorAll('product-engraving-component')
            .forEach((engraving) => {
              engraving.closeDrawer?.();
            });

          this.#updateCartQuantitySelectorButtonStates();

          return;
        }

        // No partner found: just render the result of removing the engraving.
        resetShimmer(this);

        if (removeResult.sections?.[this.sectionId]) {
          const newSectionHTML = new DOMParser().parseFromString(
            removeResult.sections[this.sectionId],
            'text/html'
          );

          const newCartHiddenItemCount =
            newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;

          const newCartItemCount = newCartHiddenItemCount
            ? parseInt(newCartHiddenItemCount, 10)
            : 0;

          this.dispatchEvent(
            new CartUpdateEvent(
              removeResult,
              this.sectionId,
              {
                itemCount: newCartItemCount,
                source: 'cart-items-component',
                sections: removeResult.sections,
              }
            )
          );

          morphSection(
            this.sectionId,
            removeResult.sections[this.sectionId]
          );
        }

        document
          .querySelectorAll('product-engraving-component')
          .forEach((engraving) => {
            engraving.closeDrawer?.();
          });

        this.#updateCartQuantitySelectorButtonStates();

        return;
      }

      /*
      * Normal product/watch removal:
      * remove the watch and its engraving partner.
      */
      const updates = {
        [cartItem.key]: 0,
      };

      if (partner) {
        updates[partner.key] = 0;

        const partnerRow = this.refs.cartItemRows.find(
          (cartItemRow) => cartItemRow.dataset.key === partner.key
        );

        if (partnerRow && !rowsToRemove.includes(partnerRow)) {
          rowsToRemove.push(partnerRow);
        }
      }

      this.#disableCartItems();

      const cartItemsComponents =
        document.querySelectorAll('cart-items-component');

      const sectionsToUpdate = new Set([this.sectionId]);

      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        updates,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const { cartTotal } = this.refs;
      cartTotal?.shimmer();

      const removeResponse = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        fetchConfig('json', { body })
      );

      const removeResult = await removeResponse.json();

      resetShimmer(this);

      if (removeResult.errors) {
        console.error('Cart update error:', removeResult.errors);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(
        removeResult.sections?.[this.sectionId] || '',
        'text/html'
      );

      const newCartHiddenItemCount =
        newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;

      const newCartItemCount = newCartHiddenItemCount
        ? parseInt(newCartHiddenItemCount, 10)
        : 0;

      this.dispatchEvent(
        new CartUpdateEvent(
          removeResult,
          this.sectionId,
          {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: removeResult.sections,
          }
        )
      );

      if (rowsToRemove.length > 0) {
        rowsToRemove.forEach((row) => {
          if (prefersReducedMotion()) return;

          row.style.setProperty(
            '--row-height',
            `${row.clientHeight}px`
          );

          row.classList.add('removing');
        });
      }

      if (removeResult.sections?.[this.sectionId]) {
        morphSection(
          this.sectionId,
          removeResult.sections[this.sectionId]
        );
      }

      document
        .querySelectorAll('product-engraving-component')
        .forEach((engraving) => {
          engraving.closeDrawer?.();
        });

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error('Error removing cart item:', error);
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
